import { useMemo } from 'react';
import { useAccessToken } from '@robinpowered/dashboard-apps-common';
import { config } from 'config';

/**
 * Minimal client for the Robin REST (core) API.
 *
 * The shell is GraphQL-first, but some data that the legacy dashboard renders
 * (e.g. the org-user object behind the profile page) is only available from the
 * REST API. This hook mirrors the pattern other apps use (see
 * `apps/schedule/src/hooks/useFetch.ts`): it attaches the `Access-Token`
 * authorization header and parses the JSON envelope.
 *
 * Build URLs from `config.apiUrl` (versioned) or `config.baseApiUrl`
 * (unversioned) at the call site. Pass `Tenant-Id` (and any other headers) via
 * `options.headers` for endpoints that are not org-scoped in the path.
 */
export type UseFetch = {
  get: <R>(
    url: string,
    params?: Record<string, string>,
    options?: RequestInit
  ) => Promise<R>;
  post: <B, R>(url: string, body: B, options?: RequestInit) => Promise<R>;
  put: <B, R>(url: string, body: B, options?: RequestInit) => Promise<R>;
  delete: <R>(url: string, options?: RequestInit) => Promise<R>;
};

export const useFetch = (): UseFetch => {
  const { accessToken } = useAccessToken(config.sessionCookieName);

  return useMemo(() => {
    const request = async <R>(
      url: string,
      options?: RequestInit
    ): Promise<R> => {
      const res = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
          Authorization: `Access-Token ${accessToken}`,
        },
      });

      if (res.status >= 200 && res.status < 300) {
        // 204 No Content (and other empty-body successes) have nothing to
        // parse — res.json() would throw. Return undefined for those.
        if (res.status === 204) {
          return undefined as unknown as R;
        }
        return res.json() as Promise<R>;
      }

      // The response body usually carries a more specific error than the
      // generic statusText, so prefer it when present.
      const message = await res.text();
      throw new Error(message || res.statusText);
    };

    const get = <R>(
      url: string,
      params?: Record<string, string>,
      options?: RequestInit
    ): Promise<R> => {
      const query = params ? `?${new URLSearchParams(params).toString()}` : '';
      return request<R>(`${url}${query}`, options);
    };

    const post = <B, R>(
      url: string,
      body: B,
      options?: RequestInit
    ): Promise<R> =>
      request<R>(url, {
        ...options,
        method: 'POST',
        body: JSON.stringify(body),
      });

    const put = <B, R>(
      url: string,
      body: B,
      options?: RequestInit
    ): Promise<R> =>
      request<R>(url, {
        ...options,
        method: 'PUT',
        body: JSON.stringify(body),
      });

    const deleteRecord = <R>(url: string, options?: RequestInit): Promise<R> =>
      request<R>(url, { ...options, method: 'DELETE' });

    return { get, post, put, delete: deleteRecord };
  }, [accessToken]);
};
